import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { WordSearchResult } from "./useWordSearch";

export interface WordSearchTab {
  id: string;
  name: string;
  pattern: string;
  selectedLength: number;
  results: WordSearchResult[];
  isLocal?: boolean; // For non-logged-in users
}

interface DbWordSearch {
  id: string;
  user_id: string;
  name: string;
  pattern: string;
  selected_length: number;
  results: Array<{ word: string; language?: number }> | string[] | null;
  created_at: string;
  updated_at: string;
}

// Helper to convert old string[] format to new WordSearchResult[]
function normalizeResults(results: Array<{ word: string; language?: number }> | string[] | null): WordSearchResult[] {
  if (!results || results.length === 0) return [];
  
  // Check if it's old format (string[])
  if (typeof results[0] === 'string') {
    return (results as string[]).map(word => ({ word, language: 1 }));
  }
  
  // New format
  return (results as Array<{ word: string; language?: number }>).map(r => ({
    word: r.word,
    language: r.language ?? 1
  }));
}

export const useWordSearchTabs = () => {
  const { user, isAuthenticated } = useAuth();
  const [tabs, setTabs] = useState<WordSearchTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load tabs from database for logged-in users
  useEffect(() => {
    const loadTabs = async () => {
      if (!isAuthenticated || !user) {
        // For non-logged-in users, create a default local tab
        const defaultTab: WordSearchTab = {
          id: "local-1",
          name: "Sökning 1",
          pattern: "",
          selectedLength: 0,
          results: [],
          isLocal: true,
        };
        setTabs([defaultTab]);
        setActiveTabId("local-1");
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("user_word_searches" as any)
          .select("*")
          .order("created_at", { ascending: true });

        if (error) throw error;

        const rows = data as unknown as DbWordSearch[];
        if (rows && rows.length > 0) {
          const loadedTabs: WordSearchTab[] = rows.map((row) => ({
            id: row.id,
            name: row.name,
            pattern: row.pattern,
            selectedLength: row.selected_length,
            results: normalizeResults(row.results),
          }));
          setTabs(loadedTabs);
          setActiveTabId(loadedTabs[0].id);
        } else {
          // Create first tab for new users
          const newTab = await createTabInternal();
          if (newTab) {
            setTabs([newTab]);
            setActiveTabId(newTab.id);
          }
        }
      } catch (error) {
        console.error("Error loading word search tabs:", error);
        // Fallback to local tab on error
        const defaultTab: WordSearchTab = {
          id: "local-1",
          name: "Sökning 1",
          pattern: "",
          selectedLength: 0,
          results: [],
          isLocal: true,
        };
        setTabs([defaultTab]);
        setActiveTabId("local-1");
      } finally {
        setIsLoading(false);
      }
    };

    // Internal create function that doesn't depend on tabs state
    const createTabInternal = async (): Promise<WordSearchTab | null> => {
      if (!user) return null;
      
      try {
        const { data, error } = await supabase
          .from("user_word_searches" as any)
          .insert({
            user_id: user.id,
            name: "Sökning 1",
            pattern: "",
            selected_length: 0,
            results: [],
          } as any)
          .select()
          .single();

        if (error) throw error;

        const row = data as unknown as DbWordSearch;
        return {
          id: row.id,
          name: row.name,
          pattern: row.pattern,
          selectedLength: row.selected_length,
          results: normalizeResults(row.results),
        };
      } catch (error) {
        console.error("Error creating initial tab:", error);
        return null;
      }
    };

    loadTabs();
  }, [isAuthenticated, user]);

  const createTab = useCallback(async (): Promise<WordSearchTab | null> => {
    const newTabNumber = tabs.length + 1;
    const newName = `Sökning ${newTabNumber}`;

    if (!isAuthenticated || !user) {
      const localTab: WordSearchTab = {
        id: `local-${Date.now()}`,
        name: newName,
        pattern: "",
        selectedLength: 0,
        results: [],
        isLocal: true,
      };
      setTabs((prev) => [...prev, localTab]);
      return localTab;
    }

    try {
      const { data, error } = await supabase
        .from("user_word_searches" as any)
        .insert({
          user_id: user.id,
          name: newName,
          pattern: "",
          selected_length: 0,
          results: [],
        } as any)
        .select()
        .single();

      if (error) throw error;

      const row = data as unknown as DbWordSearch;
      const newTab: WordSearchTab = {
        id: row.id,
        name: row.name,
        pattern: row.pattern,
        selectedLength: row.selected_length,
        results: normalizeResults(row.results),
      };

      setTabs((prev) => [...prev, newTab]);
      return newTab;
    } catch (error) {
      console.error("Error creating tab:", error);
      return null;
    }
  }, [tabs.length, isAuthenticated, user]);

  const updateTab = useCallback(
    async (tabId: string, updates: Partial<Omit<WordSearchTab, "id">>) => {
      // Check if it's a local tab by ID prefix (avoids stale closure issues)
      const isLocalTab = tabId.startsWith("local-");
      
      setTabs((prev) =>
        prev.map((tab) => (tab.id === tabId ? { ...tab, ...updates } : tab))
      );

      if (isLocalTab || !isAuthenticated) return;

      try {
        const dbUpdates: Record<string, unknown> = {};
        if (updates.name !== undefined) dbUpdates.name = updates.name;
        if (updates.pattern !== undefined) dbUpdates.pattern = updates.pattern;
        if (updates.selectedLength !== undefined)
          dbUpdates.selected_length = updates.selectedLength;
        if (updates.results !== undefined) dbUpdates.results = updates.results;

        await supabase
          .from("user_word_searches" as any)
          .update(dbUpdates as any)
          .eq("id", tabId);
      } catch (error) {
        console.error("Error updating tab:", error);
      }
    },
    [isAuthenticated]
  );

  const deleteTab = useCallback(
    async (tabId: string) => {
      const tabIndex = tabs.findIndex((t) => t.id === tabId);
      const tab = tabs.find((t) => t.id === tabId);

      if (!tab) return;

      // Don't allow deleting the last tab
      if (tabs.length <= 1) return;

      setTabs((prev) => prev.filter((t) => t.id !== tabId));

      // Switch to another tab if we're deleting the active one
      if (activeTabId === tabId) {
        const newActiveIndex = tabIndex > 0 ? tabIndex - 1 : 0;
        const remainingTabs = tabs.filter((t) => t.id !== tabId);
        if (remainingTabs.length > 0) {
          setActiveTabId(remainingTabs[newActiveIndex]?.id || remainingTabs[0].id);
        }
      }

      if (tab.isLocal || !isAuthenticated) return;

      try {
        await supabase.from("user_word_searches" as any).delete().eq("id", tabId);
      } catch (error) {
        console.error("Error deleting tab:", error);
      }
    },
    [tabs, activeTabId, isAuthenticated]
  );

  const activeTab = tabs.find((t) => t.id === activeTabId) || null;

  return {
    tabs,
    activeTab,
    activeTabId,
    setActiveTabId,
    createTab,
    updateTab,
    deleteTab,
    isLoading,
    isAuthenticated,
  };
};
